import type {
  BotSkill,
  CardId,
  DrawSource,
  Group,
  HandResult,
  PlayerStatus,
  Rank,
  ReviewedTurn,
  RuleConfig,
  SessionFormat,
  SessionPlayerState,
} from '@rummy/engine';

export const PROTOCOL_VERSION = 1;

// Re-exported so the client can talk about the game without depending on the
// engine's module layout.
export type {
  BotSkill, CardId, Group, HandResult, Rank, ReviewedTurn, RuleConfig, SessionFormat,
} from '@rummy/engine';

export type AccountId = string;
export type RoomId = string;

/** Account-synced presentation choices. Device-only controls stay local. */
export interface PlayerPreferences {
  tableSkin: 'jade' | 'midnight' | 'noir' | 'sunset' | 'seasonal';
  cardBack: 'club' | 'aurora' | 'ruby' | 'ivory';
  playerAccent: 'violet' | 'rose' | 'aqua' | 'gold';
  cardSize: 'compact' | 'comfortable' | 'large';
  deck: 'two' | 'four';
}

export interface CareerStats {
  sessions: number;
  hands: number;
  wins: number;
  drops: number;
  totalPoints: number;
}

export interface Identity {
  id: AccountId;
  handle: string;
  avatar: string;
  /** Guests are upgraded to accounts at the end of their first session (§4.1). */
  guest: boolean;
  locale: string;
  /** Present once the account has registered credentials. */
  email?: string | null;
  preferences: PlayerPreferences;
  stats: CareerStats;
}

export type PresenceState = 'online' | 'at-table' | 'away' | 'offline';

export interface FriendView {
  id: AccountId;
  handle: string;
  avatar: string;
  presence: PresenceState;
  /** Room name when at a table (§4.2). */
  roomLabel: string | null;
  roomId: RoomId | null;
}

export type TurnTimer = 30 | 45 | 60;

/** A small shared mark that makes an inviteable table feel like a place. */
export const ROOM_CRESTS = ['spade', 'lotus', 'sun', 'moon'] as const;
export type RoomCrest = (typeof ROOM_CRESTS)[number];

export interface RoomConfig {
  format: SessionFormat;
  seats: number;
  turnTimer: TurnTimer;
  /** §6.4 — off by default, host's call, visible to the whole table. */
  declarationGuard: boolean;
  /** Epoch ms; the invite link renders it in each recipient's timezone (§10). */
  startAt: number | null;
  /** Host can close seats while still sharing an invite for spectators. */
  seatLock: boolean;
  /**
   * Seat a practice opponent in every empty chair when the session starts.
   *
   * The case for it: a table set for four that two people turn up to should
   * start as the four-hander it was set up to be, rather than silently becoming
   * a different game. The case against it is that some tables would rather wait
   * for their friends, so it is the host's call and off by default.
   *
   * This is *seating*, not substitution. A player who drops mid-session stays
   * dropped and their seat is never handed to a bot.
   */
  autoFillBots: boolean;
  /** Difficulty used by auto-fill and by the one-click "fill the table". */
  fillSkill: BotSkill;
  /** Between hands, wait for the host rather than immediately dealing again. */
  pauseBetweenHands: boolean;
  /**
   * §3.8 — pool 201 lets a player who crosses 201 buy back in once, resuming at
   * the highest score still in play. Only meaningful for `pool-201`, on by
   * default because that is what the format means; a table that would rather
   * play elimination straight can turn it off.
   */
  reentry: boolean;
}

export type RoomState =
  | 'configuring'
  | 'ready'
  | 'in_hand'
  | 'hand_summary'
  | 'session_complete';

export type ConnectionState = 'connected' | 'reconnecting' | 'gone';

export interface SeatView {
  id: AccountId;
  handle: string;
  avatar: string;
  position: number;
  isHost: boolean;
  isBot: boolean;
  /** Practice opponents have a drawn character rather than a monogram. */
  character: string | null;
  /** A practice opponent's play policy, so the room can show and change it.
   *  Never set for a human seat. */
  skill?: BotSkill;
  connection: ConnectionState;
  /** Walked away mid-session. The seat is kept until the session ends so the
   *  scoreboard stays honest, then it is gone. */
  left: boolean;
  /** Card count only — never identities (§5, per-player projection). */
  cardCount: number;
  status: PlayerStatus | 'waiting';
  consecutiveAutoPlays: number;
  score: number;
  eliminated: boolean;
  /** Emote currently showing above the seat, with its expiry. */
  emote: { key: string; until: number } | null;
}

export interface RoomView {
  id: RoomId;
  /** The human name of the table. Defaults to the generated id. */
  label: string;
  /** Host-selected table mark, shared with every guest and persisted. */
  crest: RoomCrest;
  /** The generated id, always available for the URL and for saying aloud. */
  code: string;
  config: RoomConfig;
  state: RoomState;
  hostId: AccountId;
  seats: SeatView[];
  /** Late joiners, queued for the next session (§4.3). */
  queued: { id: AccountId; handle: string; avatar: string }[];
  inviteUrl: string;
  region: string;
}

/** Public, non-seating view used by an invite before the guest commits. */
export interface RoomPreview {
  id: RoomId;
  label: string;
  crest: RoomCrest;
  format: SessionFormat;
  state: RoomState;
  seated: number;
  seats: number;
  host: string;
  inviteUrl: string;
}

/**
 * Public, per-recipient events. These drive animation and the table log; they
 * are produced by the projection and never carry an identity the recipient is
 * not entitled to.
 */
export type PublicEvent =
  | { type: 'dealt' }
  | { type: 'drew'; player: AccountId; source: DrawSource; card: CardId | null; auto: boolean }
  | { type: 'discarded'; player: AccountId; card: CardId; auto: boolean }
  | { type: 'dropped'; player: AccountId; kind: 'first' | 'middle' | 'auto' }
  | { type: 'declared'; player: AccountId; valid: boolean }
  | { type: 'recycled' }
  | { type: 'completed' };

export interface SelfView {
  seat: number;
  hand: CardId[];
  /** The player's own lane arrangement, persisted server-side (§6.3). */
  groups: Group[];
  drawn: CardId | null;
  status: PlayerStatus | 'waiting';
  /** Non-authoritative convenience: how the server would score this hand now. */
  pointsIfHandEndsNow: number;
  /**
   * The hand is ready to declare. While holding fourteen this means "there is a
   * card you can throw that leaves a valid thirteen", which is the question a
   * player is actually asking — not "can all fourteen be melded".
   */
  canDeclare: boolean;
  /** The card that would go face down, when one exists. */
  finishSuggestion: CardId | null;
  /** True before this seat has completed a turn, which is what separates a
   *  20-point first drop from a 40-point middle drop (§3.7). */
  firstTurn: boolean;
  /** Points a drop would cost right now. */
  dropCost: number;
}

export interface SessionView {
  format: SessionFormat;
  handsPlayed: number;
  totalDeals: number | null;
  complete: boolean;
  winners: AccountId[];
  standings: Record<AccountId, SessionPlayerState>;
  /** §3.8 — whether this session was played with the pool-201 buy-back, so the
   *  scoreboard can replay the trajectory rather than infer the setting. */
  reentryEnabled: boolean;
  /**
   * Every hand played, with the cards.
   *
   * `reveal` is what each player was actually holding when the hand ended, and
   * is what lets the table look a finished hand back up — `results[].groups` is
   * only the arrangement, and is empty for anyone who dropped. Optional: hands
   * recorded before this was kept have results but no cards.
   */
  history: {
    ordinal: number;
    results: HandResult[];
    reveal?: Record<AccountId, CardId[]>;
    /** What was wild in this hand, and the rules it was dealt under. The cut
     *  card changes every deal, so reading a past hand against the live table's
     *  wild rank would redraw which cards were jokers. */
    wildCard?: CardId;
    wildRank?: Rank | null;
    configId?: string;
  }[];
}

export interface TableView {
  roomId: RoomId;
  handId: string;
  ordinal: number;
  phase: 'playing' | 'show' | 'complete';
  awaiting: 'draw' | 'discard';
  turn: AccountId | null;
  /** Epoch ms. The turn timer is server-authoritative and runs regardless of
   *  connection state (§8.2). */
  turnEndsAt: number | null;
  showEndsAt: number | null;
  /** Seats that still owe an arrangement in the post-declaration window
   *  (§3.5). Empty means the hand can be scored now. */
  showPending: AccountId[];
  wildCard: CardId;
  wildRank: Rank | null;
  closedCount: number;
  discardTop: CardId | null;
  /**
   * The two cards immediately under the top of the open pile, oldest last.
   *
   * A real pile has depth and shows a sliver of what is under it, and that
   * sliver is the cheapest table intel there is — visible without opening a
   * panel. Nothing is revealed by it: every card here arrived via a public
   * `CARD_DISCARDED` event, so any client could already reconstruct the pile
   * from `publicHistory`.
   */
  discardBelow: CardId[];
  /** How deep the open pile is, so the stack reads as a pile rather than a card. */
  discardDepth: number;
  discardCount: number;
  seats: SeatView[];
  you: SelfView | null;
  /**
   * The turns where this player's discard diverged most from the expert policy,
   * present only once the hand is complete and only for the player it is about.
   * Post-hand by construction: it cannot inform a decision still to be made.
   */
  review?: ReviewedTurn[];
  results: HandResult[] | null;
  /** Revealed hands, present only once the hand is complete. */
  reveal: Record<AccountId, CardId[]> | null;
  deckCommit: string;
  deckReveal: { order: CardId[]; nonce: string } | null;
  guard: boolean;
  /**
   * Every public event in the current hand, in order. This is deliberately
   * separate from `events`, which is the small just-happened batch used for
   * animation. It lets a returning player reconstruct the information that
   * was visible at the table without ever receiving a private card identity.
   */
  publicHistory?: PublicEvent[];
  events: PublicEvent[];
  session: SessionView | null;
  /**
   * The rules this table is playing. Deck count depends on the number of seats
   * (§3.1, as amended in the engine's config), and the client renders and hints
   * against whatever the server says rather than a constant of its own.
   */
  rules: RuleConfig;
}

export type GameAction =
  | { type: 'draw'; source: DrawSource }
  | { type: 'discard'; card: CardId }
  | { type: 'arrange'; groups: Group[] }
  | { type: 'drop' }
  | { type: 'declare'; finishCard: CardId; groups: Group[] }
  | { type: 'show'; groups: Group[] };

export type ClientMessage =
  | { t: 'hello'; token: string | null; handle?: string; avatar?: string; locale: string }
  | { t: 'set-profile'; handle: string; avatar: string }
  | { t: 'set-preferences'; preferences: PlayerPreferences }
  | { t: 'create-room'; config?: Partial<RoomConfig> }
  | { t: 'preview-room'; roomId: RoomId }
  | { t: 'join-room'; roomId: RoomId }
  /** Watches an active room without taking a seat or receiving hand identities. */
  | { t: 'spectate-room'; roomId: RoomId }
  | { t: 'leave-room' }
  | { t: 'configure-room'; patch: Partial<RoomConfig> }
  | { t: 'rename-room'; name: string }
  | { t: 'set-room-crest'; crest: RoomCrest }
  /** `skill` picks the practice-opponent's play strength directly; omitted, it
   *  falls back to whatever personality `character` (or the next open one)
   *  carries. The two are independent so a table can run several bots at the
   *  same difficulty without needing several differently-skilled characters. */
  | { t: 'add-bot'; character?: string; skill?: BotSkill }
  /** Seat a practice opponent in every empty chair, in one action. */
  | { t: 'fill-seats'; skill?: BotSkill }
  /** Change one seated practice opponent's play strength. */
  | { t: 'set-bot-skill'; accountId: AccountId; skill: BotSkill }
  | { t: 'remove-seat'; accountId: AccountId }
  | { t: 'start-session' }
  | { t: 'rematch' }
  /** Skip the hand-summary countdown. */
  | { t: 'continue' }
  | { t: 'invite'; accountId: AccountId }
  | { t: 'emote'; key: string }
  /** Every action carries a UUID and the last-seen sequence number (§5). */
  | { t: 'action'; id: string; lastSeq: number; action: GameAction }
  | { t: 'resync'; lastSeq: number }
  | { t: 'ping' };

export type ServerMessage =
  | { t: 'welcome'; identity: Identity; token: string; protocol: number; region: string }
  | { t: 'lobby'; friends: FriendView[]; rooms: RoomSummary[] }
  | { t: 'room'; seq: number; room: RoomView | null }
  | { t: 'room-preview'; preview: RoomPreview | null }
  /**
   * A private acknowledgement sent before the full table snapshot. A draw is
   * already committed on the server at this point; the acknowledgement simply
   * lets the drawer render that card without waiting for every recipient's
   * derived table view to be calculated.
   */
  | { t: 'draw-ack'; id: string; handId: string; card: CardId }
  /** A private receipt for every accepted move. Unlike a table broadcast, it
   * can only settle the action that created it. */
  | { t: 'action-ack'; id: string; action: GameAction['type'] }
  | { t: 'table'; seq: number; view: TableView }
  | { t: 'notification'; kind: 'invite'; from: FriendView; roomId: RoomId; roomLabel: string }
  | { t: 'error'; code: string; message: string }
  | { t: 'pong'; at: number };

/**
 * How a finished session at this table went.
 *
 * The club floor showed who was online and nothing about what happened. A
 * friend graph built by playing together should be able to say "Thursday: Meera
 * won the pool, you were second" — it is the cheapest retention there is, and
 * every field here is already stored.
 */
export interface RoomResult {
  /** Winners' handles, usually one. */
  winners: string[];
  /** Where the viewer finished, 1-based. `null` if they were not in it. */
  yourPlace: number | null;
  players: number;
  handsPlayed: number;
  at: number;
}

export interface RoomSummary {
  id: RoomId;
  label: string;
  format: SessionFormat;
  seated: number;
  seats: number;
  /** The last completed session here, when there has been one. */
  lastResult?: RoomResult | null;
  state: RoomState;
  lastPlayedAt: number;
}

/**
 * Practice opponents, in the order seats fill.
 *
 * Ten, not five. A six-seat table needs five opponents to fill it around one
 * human, so a five-strong roster was the exact ceiling with nothing to spare —
 * and no room at all for a table that wants two of something. Beyond the
 * roster, characters repeat with a numbered handle rather than the seat being
 * refused: identity and seat are separate things.
 */
export const BOT_CHARACTERS = [
  'nila', 'arun', 'devi', 'kabir', 'meera',
  'ravi', 'sana', 'jai', 'ila', 'tara',
] as const;
export type BotCharacter = (typeof BOT_CHARACTERS)[number];

/**
 * Legacy avatar keys from the pre-DiceBear portrait set. Still accepted so an
 * old account keeps its face; new avatars are `db:<seed>` DiceBear seeds.
 */
export const AVATARS = [
  'asha', 'ravi', 'leela', 'tara', 'iqbal', 'noor', 'sam',
] as const;

export type Avatar = (typeof AVATARS)[number];

/**
 * An avatar is either a DiceBear seed (`db:<seed>`, chosen or randomised by
 * the player) or a legacy key. The seed grammar is deliberately tight — it
 * ends up in DOM attributes and in the event log.
 */
export function isValidAvatar(value: string): boolean {
  return /^db:[A-Za-z0-9_-]{1,40}$/.test(value) || (AVATARS as readonly string[]).includes(value);
}

export function randomAvatar(): string {
  return `db:${Math.random().toString(36).slice(2, 10)}`;
}
